/**
 * fetch wrapper that recovers from an expired backend bearer.
 *
 * On a 401, it calls `refresh()` once (which silently re-acquires a token via
 * MSAL's cached refresh token), rebuilds the `Authorization` header with the
 * fresh token, and retries the request a single time. Without a `refresh`
 * callback — or when refresh yields no token — the original 401 is returned
 * unchanged, so callers keep their existing error handling.
 *
 * This is the reactive half of the token-refresh fix (AIVA-5037 / AIVA-4869):
 * it guarantees the first request after an expired token recovers instead of
 * surfacing a 401 to the user. For SSE/streaming POSTs the 401 is detected via
 * `response.status` before the body is read, so swapping the response is safe.
 *
 * Mirrors the connector-side retry in `src/services/serviceFetch.ts`.
 *
 * Telemetry: like `serviceFetch`, every call emits a `perf.api.call` event
 * (templated path + status + client duration), `error.api.4xx`/`error.api.5xx`
 * on a bad status, and `error.network` when the fetch throws — so calls to our
 * own backends (AI API, add-in API) appear on the unified Logs page. The
 * `backend` tag is inferred from the URL via the configured base URLs.
 */
import { config } from "../config/environment";
import { emitApiTelemetry, emitNetworkError, perfNow, sanitisePath } from "../../telemetry/apiTelemetry";
import type { Backend } from "../../telemetry/types";
import { ensureAccess } from "../../services/studio/accessGuard";

export type RefreshTokenFn = () => Promise<string | null>;

/** Per-call knobs. `skipAccessCheck` bypasses the pre-request access
 * re-validation — set only by the settings-validation fetch itself (which IS the
 * check) and by the initial settings load, to avoid recursing. */
export interface FetchWithRefreshOptions {
  skipAccessCheck?: boolean;
}

/** Map a request URL to its telemetry `backend` tag using the configured base
 * URLs. Our own .NET non-AI surfaces (add-in + web-portal) share the "addin"
 * tag; the Python AI API is "ai". Anything unrecognised defaults to "ai", the
 * most common caller (chat). */
function inferBackend(url: string): Backend {
  if (config.aiApiBaseUrl && url.startsWith(config.aiApiBaseUrl)) return "ai";
  if (config.addinApiBaseUrl && url.startsWith(config.addinApiBaseUrl)) return "addin";
  if (config.nonAiApiBaseUrl && url.startsWith(config.nonAiApiBaseUrl)) return "addin";
  return "ai";
}

export async function fetchWithRefresh(
  url: string,
  init: RequestInit,
  refresh?: RefreshTokenFn,
  fetchImpl: typeof fetch = fetch,
  opts: FetchWithRefreshOptions = {},
): Promise<Response> {
  // Re-validate Studio access before every backend call so a mid-session change
  // (user deactivated/archived, persona/channel/connection access changed) blocks
  // the surface instead of running against stale access. Throws AccessRevokedError
  // when blocked; the settings-validation fetch and initial load opt out.
  if (!opts.skipAccessCheck) await ensureAccess();

  const method = (init.method ?? "GET").toUpperCase();
  const templatedPath = sanitisePath(url);
  const backend = inferBackend(url);
  const started = perfNow();

  let res: Response;
  try {
    res = await fetchImpl(url, init);
  } catch (err) {
    emitNetworkError(templatedPath, method, started, backend, err);
    throw err;
  }

  if (res.status !== 401 || !refresh) {
    emitApiTelemetry(templatedPath, method, started, res.status, backend);
    return res;
  }

  const freshToken = await refresh();
  if (!freshToken) {
    emitApiTelemetry(templatedPath, method, started, res.status, backend);
    return res;
  }

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
    Authorization: `Bearer ${freshToken}`,
  };
  try {
    res = await fetchImpl(url, { ...init, headers });
  } catch (err) {
    emitNetworkError(templatedPath, method, started, backend, err);
    throw err;
  }
  emitApiTelemetry(templatedPath, method, started, res.status, backend);
  return res;
}
